import React, { useEffect, useMemo, useState } from "react";
import {
  fetchDeliveryProjects,
  fetchProjectDocumentFolders,
  fetchProjectSummary,
  fetchProjectAuditLog,
  fetchProjectClaims,
} from "../../services/projectDelivery";
import { useNavigate } from "react-router-dom";

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 shadow-sm">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-2xl font-bold text-black dark:text-white">
        {value}
      </div>
    </div>
  );
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    green:
      "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900",
    red:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900",
    yellow:
      "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900",
    blue:
      "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900",
    gray:
      "bg-gray-50 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-gray-300 dark:border-neutral-700",
  };

  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function getStatusTone(row) {
  switch (row.status) {
    case "received":
      return "green";
    case "duplicate":
      return "yellow";
    case "manual_scanned":
      return "blue";
    case "manual_not_scanned":
    case "missing":
    default:
      return "red";
  }
}

function formatAuditAction(actionType) {
  const map = {
    scan_created: "Skenirano",
    manual_entry_created: "Ručni unos",
    override_set_scanned: "Ručno označeno skenirano",
    override_set_not_scanned: "Ručno označeno nije skenirano",
    override_removed: "Uklonjen override",
    session_closed: "Zatvorena sesija",
    claim_created: "Kreirana reklamacija",
  };

  return map[actionType] || actionType || "-";
}

export default function QAQCDashboardPage() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedProjectKey, setSelectedProjectKey] = useState("");
  const [selectedRevision, setSelectedRevision] = useState("");
  const [selectedFolderKey, setSelectedFolderKey] = useState("");

  const [batchData, setBatchData] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

const loadProjects = async () => {
  try {
    setLoading(true);

    console.log("Loading delivery projects...");
    const projectList = await fetchDeliveryProjects();

    console.log("Delivery projects response:", projectList);

    setProjects(Array.isArray(projectList) ? projectList : []);
  } catch (err) {
    console.error("loadProjects error full:", err);
    console.error("response:", err?.response);
    console.error("status:", err?.response?.status);
    console.error("data:", err?.response?.data);
  } finally {
    setLoading(false);
  }
};

const handleProjectChange = async (value) => {
  console.log("Selected project value:", value);

  setSelectedProjectKey(value);
  setSelectedRevision("");
  setSelectedFolderKey("");
  setFolders([]);
  setBatchData(null);
  setRecentActivity([]);

  if (!value) return;

  try {
    setLoading(true);

    const data = await fetchProjectDocumentFolders(value);

    console.log("Document folders response:", data);
    console.log("First folder:", data?.[0]);
    console.log("Folder keys:", Object.keys(data?.[0] || {}));

    setFolders(data || []);
  } catch (err) {
    console.error("load folders error:", err);
    setFolders([]);
  } finally {
    setLoading(false);
  }
};

  const revisionOptions = useMemo(() => {
    const revisions = new Set();

    folders.forEach((f) => {
      const label = String(f.label || "");
      const revision = label.split("/")[0]?.trim();
      if (revision) revisions.add(revision);
    });

    return Array.from(revisions).sort();
  }, [folders]);

  const filteredFolders = useMemo(() => {
    if (!selectedRevision) return folders;

    return folders.filter((f) => {
      const label = String(f.label || "");
      const revision = label.split("/")[0]?.trim();
      return revision === selectedRevision;
    });
  }, [folders, selectedRevision]);

  const selectedProjectLabel = useMemo(() => {
    return (
      projects.find((p) => p.projectKey === selectedProjectKey)?.label || ""
    );
  }, [projects, selectedProjectKey]);

  const selectedFolderLabel = useMemo(() => {
    return (
      filteredFolders.find((f) => f.key === selectedFolderKey)?.label || ""
    );
  }, [filteredFolders, selectedFolderKey]);

  const handleLoadBatch = async () => {
    if (!selectedProjectKey || !selectedFolderKey) return;

    try {
      setLoading(true);

      const [summaryRes, auditRes, claimsRes] = await Promise.all([
        fetchProjectSummary(selectedProjectKey, selectedFolderKey),
        fetchProjectAuditLog(selectedProjectKey, 100),
        fetchProjectClaims(selectedProjectKey),
      ]);

      const allClaims = claimsRes || [];
      const rows = summaryRes?.rows || [];

      const claimMap = new Map();
      allClaims.forEach((c) => {
        const key = String(c.board_code || "").trim().toLowerCase();
        claimMap.set(key, (claimMap.get(key) || 0) + 1);
      });

      const rowsWithClaims = rows.map((row) => {
        const key = String(row.board_code || "").trim().toLowerCase();
        const claimCount = claimMap.get(key) || 0;

        return {
          ...row,
          claimCount,
          hasClaim: claimCount > 0,
        };
      });

      setBatchData({
        summary: summaryRes?.summary || null,
        rows: rowsWithClaims,
        unknownScans: summaryRes?.unknownScans || [],
        claims: allClaims,
      });

      setRecentActivity(auditRes || []);
    } catch (err) {
      console.error("handleLoadBatch error:", err);
      setBatchData(null);
      setRecentActivity([]);
    } finally {
      setLoading(false);
    }
  };

  const overview = useMemo(() => {
    const s = batchData?.summary || {};
    const claims = batchData?.claims || [];
    const rows = batchData?.rows || [];

    const openClaims = claims.filter((c) => c.status === "open").length;
    const claimedItems = rows.filter((r) => r.hasClaim).length;

    return {
      expected: Number(s.expectedCount || 0),
      received: Number(s.receivedCount || 0),
      missing: Number(s.missingCount || 0),
      duplicates: Number(s.duplicateCount || 0),
      unknown: Number(s.unknownCount || 0),
      overrides: Number(s.manualOverrideCount || 0),
      claims: claims.length,
      openClaims,
      claimedItems,
      readiness: Number(s.readinessPercent || 0),
    };
  }, [batchData]);

  const problemRows = useMemo(() => {
    return (batchData?.rows || [])
      .filter((r) => r.status !== "received" || r.hasClaim)
      .sort((a, b) => {
        const scoreA =
          (a.status === "missing" ? 10 : 0) +
          (a.status === "duplicate" ? 5 : 0) +
          (a.status === "manual_not_scanned" ? 8 : 0) +
          (a.hasClaim ? 12 : 0);

        const scoreB =
          (b.status === "missing" ? 10 : 0) +
          (b.status === "duplicate" ? 5 : 0) +
          (b.status === "manual_not_scanned" ? 8 : 0) +
          (b.hasClaim ? 12 : 0);

        return scoreB - scoreA;
      });
  }, [batchData]);

  const latestClaims = useMemo(() => {
    return [...(batchData?.claims || [])]
      .sort((a, b) => {
        const da = new Date(a.reported_at || a.updated_at).getTime();
        const db = new Date(b.reported_at || b.updated_at).getTime();
        return db - da;
      })
      .slice(0, 30);
  }, [batchData]);

  const filteredRecentActivity = useMemo(() => {
    return [...recentActivity]
      .sort((a, b) => {
        const da = new Date(a.created_at).getTime();
        const db = new Date(b.created_at).getTime();
        return db - da;
      })
      .slice(0, 30);
  }, [recentActivity]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-black dark:text-white">
          QA/QC Dashboard
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Pregled kontrole kvaliteta i kvantiteta po konkretnom proizvodnom fajlu,
          sa aktivnostima i reklamacijama.
        </p>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <select
            value={selectedProjectKey}
            onChange={(e) => handleProjectChange(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">-- Izaberi projekat --</option>
            {projects.map((p) => (
              <option key={p.projectKey} value={p.projectKey}>
                {p.label}
              </option>
            ))}
          </select>

          <select
            value={selectedRevision}
            onChange={(e) => {
              setSelectedRevision(e.target.value);
              setSelectedFolderKey("");
              setBatchData(null);
              setRecentActivity([]);
            }}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
            disabled={!selectedProjectKey}
          >
            <option value="">-- Izaberi reviziju --</option>
            {revisionOptions.map((rev) => (
              <option key={rev} value={rev}>
                {rev}
              </option>
            ))}
          </select>

          <select
            value={selectedFolderKey}
            onChange={(e) => {
              setSelectedFolderKey(e.target.value);
              setBatchData(null);
              setRecentActivity([]);
            }}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
            disabled={!selectedProjectKey}
          >
            <option value="">-- Izaberi fajl --</option>
            {filteredFolders.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleLoadBatch}
            disabled={!selectedProjectKey || !selectedFolderKey}
            className="w-full px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium disabled:opacity-50"
          >
            Učitaj pregled
          </button>
        </div>

        {selectedProjectLabel && (
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            <span className="font-medium text-black dark:text-white">
              Projekat:
            </span>{" "}
            {selectedProjectLabel}
            {selectedFolderLabel ? (
              <>
                {" "}
                <span className="mx-2">•</span>
                <span className="font-medium text-black dark:text-white">
                  Fajl:
                </span>{" "}
                {selectedFolderLabel}
              </>
            ) : null}
          </div>
          
        )}
<button
  type="button"
  onClick={() =>
    navigate(
      `/projects/qa-qc/${encodeURIComponent(
        selectedProjectKey
      )}?documentFolderKey=${encodeURIComponent(selectedFolderKey)}`
    )
  }
  className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium"
>
  Otvori QA/QC pregled
</button>
        
      </div>

      {batchData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-8 gap-4">
            <StatCard label="Očekivano" value={overview.expected} />
            <StatCard label="Primljeno" value={overview.received} />
            <StatCard label="Nedostaje" value={overview.missing} />
            <StatCard label="Duplikati" value={overview.duplicates} />
            <StatCard label="Nepoznato" value={overview.unknown} />
            <StatCard label="Readiness %" value={overview.readiness} />
            <StatCard label="Reklamacije" value={overview.claims} />
            <StatCard label="Open claims" value={overview.openClaims} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
                <h2 className="text-lg font-semibold text-black dark:text-white">
                  Poslednje aktivnosti
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[1000px]">
                  <thead className="bg-gray-50 dark:bg-neutral-800">
                    <tr className="border-b border-gray-200 dark:border-neutral-700">
                      <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                        Vreme
                      </th>
                      <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                        Akcija
                      </th>
                      <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                        Stavka
                      </th>
                      <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                        Korisnik
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecentActivity.map((item) => (
                      <tr
                        key={`${item.project_key}-${item.id}`}
                        className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition"
                      >
                        <td className="p-3 text-black dark:text-white">
                          {new Date(item.created_at).toLocaleString()}
                        </td>
                        <td className="p-3">
                          <Badge tone="gray">
                            {formatAuditAction(item.action_type)}
                          </Badge>
                        </td>
                        <td className="p-3 text-black dark:text-white">
                          {item.item_code || "-"}
                        </td>
                        <td className="p-3 text-black dark:text-white">
                          {item.user_name || item.user_email || item.user_id || "-"}
                        </td>
                      </tr>
                    ))}

                    {!loading && filteredRecentActivity.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="p-8 text-center text-gray-500 dark:text-gray-400"
                        >
                          Nema aktivnosti.
                        </td>
                      </tr>
                    )}

                    {loading && (
                      <tr>
                        <td
                          colSpan={4}
                          className="p-8 text-center text-gray-500 dark:text-gray-400"
                        >
                          Učitavanje...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
                <h2 className="text-lg font-semibold text-black dark:text-white">
                  Poslednje reklamacije
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[1000px]">
                  <thead className="bg-gray-50 dark:bg-neutral-800">
                    <tr className="border-b border-gray-200 dark:border-neutral-700">
                      <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                        Vreme
                      </th>
                      <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                        Tabla
                      </th>
                      <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                        Tip
                      </th>
                      <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestClaims.map((item) => (
                      <tr
                        key={`claim-${item.id}`}
                        className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition"
                      >
                        <td className="p-3 text-black dark:text-white">
                          {new Date(item.reported_at || item.updated_at).toLocaleString()}
                        </td>
                        <td className="p-3 text-black dark:text-white font-medium">
                          {item.board_code}
                        </td>
                        <td className="p-3 text-black dark:text-white">
                          {item.issue_type || "-"}
                        </td>
                        <td className="p-3">
                          <Badge
                            tone={
                              item.status === "resolved"
                                ? "green"
                                : item.status === "in_review"
                                ? "blue"
                                : item.status === "rejected"
                                ? "red"
                                : "yellow"
                            }
                          >
                            {item.status || "open"}
                          </Badge>
                        </td>
                      </tr>
                    ))}

                    {!loading && latestClaims.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="p-8 text-center text-gray-500 dark:text-gray-400"
                        >
                          Nema reklamacija.
                        </td>
                      </tr>
                    )}

                    {loading && (
                      <tr>
                        <td
                          colSpan={4}
                          className="p-8 text-center text-gray-500 dark:text-gray-400"
                        >
                          Učitavanje...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
              <h2 className="text-lg font-semibold text-black dark:text-white">
                Stavke sa problemima
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1200px]">
                <thead className="bg-gray-50 dark:bg-neutral-800">
                  <tr className="border-b border-gray-200 dark:border-neutral-700">
                    <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                      Kod
                    </th>
                    <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                      Fajl
                    </th>
                    <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                      Status
                    </th>
                    <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                      Nedostaje
                    </th>
                    <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                      Real skenovi
                    </th>
                    <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                      Reklamacija
                    </th>
                    <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                      Akcija
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {problemRows.map((item, idx) => (
                    <tr
                      key={`${item.board_code}-${idx}`}
                      className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition"
                    >
                      <td className="p-3 text-black dark:text-white font-medium">
                        {item.board_code}
                      </td>
                      <td className="p-3 text-black dark:text-white">
                        {item.file_name}
                      </td>
                      <td className="p-3">
                        <Badge tone={getStatusTone(item)}>
                          {item.status_label}
                        </Badge>
                      </td>
                      <td className="p-3 text-black dark:text-white">{item.missing}</td>
                      <td className="p-3 text-black dark:text-white">
                        {item.qty_scanned_real}
                      </td>
                      <td className="p-3">
                        {item.hasClaim ? (
                          <Badge tone="yellow">
                            {item.claimCount > 1
                              ? `${item.claimCount} reklamacije`
                              : "Reklamacija"}
                          </Badge>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/projects/qa-qc/${encodeURIComponent(
                                selectedProjectKey
                              )}?documentFolderKey=${encodeURIComponent(
                                selectedFolderKey
                              )}`
                            )
                          }
                          className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-700 text-black dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800"
                        >
                          Otvori batch
                        </button>
                      </td>
                    </tr>
                  ))}

                  {!loading && problemRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="p-8 text-center text-gray-500 dark:text-gray-400"
                      >
                        Nema problematičnih stavki.
                      </td>
                    </tr>
                  )}

                  {loading && (
                    <tr>
                      <td
                        colSpan={7}
                        className="p-8 text-center text-gray-500 dark:text-gray-400"
                      >
                        Učitavanje...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!batchData && !loading && (
        <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-8 text-center text-gray-500 dark:text-gray-400">
          Izaberi projekat, reviziju i fajl, pa klikni na <span className="font-medium text-black dark:text-white">Učitaj pregled</span>.
        </div>
      )}
    </div>
  );
}